import type { Mailbox } from '@/services/mailboxes'

/**
 * SA-755: the one rule for a mailbox row's address line — which account the
 * mailbox reads. `mismatch` is true when the connected account is not the
 * typed address (a mailbox connected before SA-755, or an edit); the row
 * then carries a "Reads a different account" badge. Case-insensitive.
 */
export interface AccountLine {
  text: string
  mismatch: boolean
}

const NO_ADDRESS = 'no group address set'

export function accountLine(
  mb: Pick<Mailbox, 'connected' | 'email_address' | 'connected_account'> &
    Partial<Pick<Mailbox, 'account_confirmed' | 'account_flagged'>>,
): AccountLine {
  const typed = mb.email_address?.trim() || null
  if (!mb.connected) return { text: typed ?? NO_ADDRESS, mismatch: false }
  const account = mb.connected_account?.trim() || null
  if (account === null) {
    return { text: `${typed ?? NO_ADDRESS} · connected account unknown`, mismatch: false }
  }
  if (typed === null || typed.toLowerCase() === account.toLowerCase()) {
    return { text: `Reads ${account}`, mismatch: false }
  }
  if (mb.account_confirmed === true) {
    return { text: `Reads ${account} · mail to ${typed}`, mismatch: false }
  }
  // SA-1422: the served flag decides; an older server sends none, so compare.
  const flagged = mb.account_flagged ?? true
  if (!flagged) return { text: `Reads ${account} · mail to ${typed}`, mismatch: false }
  return { text: `Reads ${account}, not ${typed}`, mismatch: true }
}

/** SA-1422: the flag a mailbox carries when it reads an account other than its address, on the
 *  list row and the pane alike. */
export const DIFFERENT_ACCOUNT = 'Reads a different account'

/** SA-1422: the tip on the "Reads a different account" flag. */
export function flagTip(mb: Pick<Mailbox, 'email_address' | 'connected_account'>): string {
  const account = mb.connected_account?.trim() ?? ''
  const typed = mb.email_address?.trim() ?? ''
  return `This mailbox reads ${account}'s inbox, but its address is ${typed}. If mail to ${typed} arrives in that inbox, choose Confirm. Otherwise choose Change account and sign in as the right account.`
}

/** SA-1422: the Change account dialog's lines, in order. */
export function changeAccountLines(mb: Pick<Mailbox, 'connected_account' | 'status'>): string[] {
  const lines = [
    'This mailbox keeps its threads. Mail that arrived in the new account before now is not loaded; use Load earlier mail for it.',
  ]
  const account = mb.connected_account?.trim()
  if (account) lines.push(`Replies to threads that came in through ${account} can't be sent from the new account.`)
  if (mb.status === 'paused') lines.push('It stays paused until you resume it.')
  return lines
}
